import React from 'react';
import { useTranslation } from 'react-i18next';
import {
    EditablePriceScale,
    PriceScale,
    PricingDiscount,
} from '../../../api/asset/assetApiTypes';
import InputField from '../../../components/ui/InputField';
import PriceScaleTable from '../../../components/PriceScaleTable';
import NetGrossField from '../../../components/pricing/NetGrossField';
import PriceEntryNote from '../../../components/pricing/PriceEntryNote';
import { usePriceListTaxRate } from '../../../hooks/usePriceEntry';
import { useGetCustomerClassesQuery } from '../../../api/organization/organizationApiSlice';
import { applyClassPrice, classRowFor, generalRows } from '../classRateRows';
import {
    SUB_DAY_UNITS,
    applySubDayPrice,
    isSubDayRow,
    subDayRowFor,
} from '../subDayRateRows';
import AssetDetailSection from './AssetDetailSection';
import { spaceOf } from '../../../utils/useCases';

interface PricingSectionProps {
    priceCategoryName: string | undefined;
    priceScales: PriceScale[] | undefined;
    pricingDiscounts: PricingDiscount[] | null | undefined;
    useCase?: string;
    mode: 'view' | 'edit' | 'create';
    // Edit / create form state
    editPriceScales?: EditablePriceScale[];
    onPriceScalesChange?: (scales: EditablePriceScale[]) => void;
    editDiscounts?: PricingDiscount[];
    onDiscountsChange?: (discounts: PricingDiscount[]) => void;
    /**
     * Number of assets sharing the same group/price category. When > 1 in
     * edit mode, the UI warns that base-price changes affect all of them.
     */
    groupAssetCount?: number;
    /**
     * Whether the long-stay discount tiers render. They live on the OBJECT
     * (`asset.pricing_discounts`), not on the price sheet, so the product-level
     * price dialog (#2923) edits the sheet without them. Default true.
     */
    showDiscounts?: boolean;
    /**
     * In edit mode, show the prices as the read-only table instead of inputs —
     * the object page, where prices are the PRODUCT's and set in Services
     * (#2923). Default false.
     */
    pricesReadOnly?: boolean;
    /**
     * Option b (PO 2026-09-28): the long-stay discounts are the PRODUCT's too,
     * maintained in the Services price dialog and written identically to every
     * object. On the object page they render read-only (from
     * `pricingDiscounts`) even in edit mode. Default false.
     */
    discountsReadOnly?: boolean;
    /**
     * The price list's id, for the VAT rate net entry and the net/VAT/gross
     * table convert at (#2967, `usePriceListTaxRate`).
     */
    priceCategoryId?: string | null;
}

const PricingSection: React.FC<PricingSectionProps> = ({
    priceCategoryName,
    priceScales,
    pricingDiscounts,
    useCase,
    mode,
    editPriceScales,
    onPriceScalesChange,
    editDiscounts,
    onDiscountsChange,
    groupAssetCount,
    showDiscounts = true,
    pricesReadOnly = false,
    discountsReadOnly = false,
    priceCategoryId,
}) => {
    const { t } = useTranslation();
    const rate = usePriceListTaxRate(priceCategoryId);
    // #1808. A legacy tenant's endpoint answers 409, so this stays empty and
    // the class-rate block below never renders — which is correct, not merely
    // convenient: customer classes are an operational-schema concept.
    const { data: customerClasses = [] } = useGetCustomerClassesQuery();
    const isEditing = mode === 'edit';
    const isCreating = mode === 'create';
    // The stay pricing rule: a nightly price with long-stay discounts.
    const isStayPricing = spaceOf(useCase)?.flow === 'stay';

    const dayTypeLabels: Record<string, string> = {
        weekday: t('asset_detail.fields.weekday'),
        weekend: t('asset_detail.fields.weekend'),
    };

    const formatDiscountRange = (nights: [number, number]): string => {
        if (nights[1] < 0 || nights[1] < nights[0]) {
            return `${t('asset_detail.fields.from')} ${nights[0]}`;
        }
        return `${nights[0]}–${nights[1]}`;
    };

    // ─── VIEW MODE ─────────────────────────────────────────────────────────
    const hasScales = (priceScales ?? []).length > 0;
    const hasDiscounts = pricingDiscounts && pricingDiscounts.length > 0;

    // The read-only tier table — the view mode's and (option b) the object
    // page's, where the discounts are the product's and not editable here.
    const discountTable = (tiers: PricingDiscount[]) => (
        <div className={'mt-4 overflow-x-auto'}>
            <table className={'w-full text-sm'}>
                <thead>
                    <tr className={'border-b border-foreground/10'}>
                        <th className={'text-left py-2 text-foreground/60 font-medium'}>
                            {isStayPricing
                                ? t('asset_detail.fields.nights')
                                : t('asset_detail.fields.days')}
                        </th>
                        <th className={'text-right py-2 text-foreground/60 font-medium'}>
                            {t('asset_detail.fields.discount')}
                        </th>
                    </tr>
                </thead>
                <tbody>
                    {tiers.map((discount, i) => (
                        <tr key={i} className={'border-b border-foreground/5'}>
                            <td className={'py-2 text-foreground'}>
                                {formatDiscountRange(discount.nights)}
                            </td>
                            <td className={'py-2 text-foreground font-medium text-right'}>
                                -{discount.discount}%
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );

    if (!isEditing && !isCreating) {
        return (
            <AssetDetailSection title={t('asset_detail.sections.pricing')}>
                {/* Price category is a concept the backend doesn't currently
                    populate on this endpoint — hide it in all modes to avoid
                    showing a field that's always empty or wrong. The Services
                    price view names it, from its own endpoint. */}

                {/* Row rendering shared with the Services price view. */}
                <PriceScaleTable priceScales={priceScales} useCase={useCase} priceCategoryId={priceCategoryId} />

                {hasDiscounts && discountTable(pricingDiscounts)}

                {!hasScales && !hasDiscounts && !priceCategoryName && (
                    <p className={'text-sm text-foreground/40 italic mt-2'}>
                        {t('asset_detail.empty_states.pricing')}
                    </p>
                )}
            </AssetDetailSection>
        );
    }

    // ─── EDIT / CREATE MODE ────────────────────────────────────────────────
    const scales = editPriceScales ?? [];
    const discounts = editDiscounts ?? [];

    // Base-price editors — apartments have a single nightly scale, venues
    // have weekday + weekend scales.
    // An emptied field becomes NaN, NOT 0: "deliberately free" and "price
    // forgotten" must stay distinguishable, or clearing the field silently
    // saves a free booking. NaN never reaches the API — both save paths
    // reject it via priceScaleAmountsValid; an explicit typed 0 is valid.
    const handleScalePriceChange = (index: number, newPrice: string) => {
        const parsed = newPrice === '' ? NaN : parseFloat(newPrice);
        if (newPrice !== '' && Number.isNaN(parsed)) return;
        const updated = scales.map((s, i) => i === index ? { ...s, price: parsed } : s);
        onPriceScalesChange?.(updated);
    };

    const scaleLabel = (scale: EditablePriceScale): string => {
        if (scale.days && dayTypeLabels[scale.days]) {
            return dayTypeLabels[scale.days];
        }
        if (isStayPricing) {
            return t('asset_detail.fields.price_per_night');
        }
        return t('asset_detail.fields.price');
    };

    // Discount editors — apartments only.
    // Internally the editor uses a split-point model: each tier is represented
    // by its starting night. Ranges are derived (tier[i] covers startNight
    // to tier[i+1].startNight - 1; the last tier is open-ended). This removes
    // the need for a `-1` sentinel in the UI and makes sequentiality a
    // structural invariant instead of a rule the user has to satisfy.
    type SplitPoint = { startNight: number, discount: number };

    const splitPoints: SplitPoint[] = discounts.map((d) => ({
        startNight: d.nights[0],
        discount: d.discount,
    }));

    const emitSplitPoints = (sps: SplitPoint[]) => {
        const wire: PricingDiscount[] = sps.map((sp, i) => {
            const isLast = i === sps.length - 1;
            const to = isLast ? -1 : sps[i + 1].startNight - 1;
            return { nights: [sp.startNight, to] as [number, number], discount: sp.discount };
        });
        onDiscountsChange?.(wire);
    };

    const rowErrorKey = (i: number): string | null => {
        const sp = splitPoints[i];
        if (sp.startNight < 1 || !Number.isFinite(sp.startNight)) {
            return 'asset_detail.validation.row.start_min';
        }
        if (i > 0 && sp.startNight <= splitPoints[i - 1].startNight) {
            return 'asset_detail.validation.row.not_sequential';
        }
        if (sp.discount < 0 || sp.discount > 100 || !Number.isFinite(sp.discount)) {
            return 'asset_detail.validation.row.invalid_discount';
        }
        return null;
    };

    const handleStartChange = (index: number, value: string) => {
        const parsed = value === '' ? 0 : parseInt(value, 10);
        if (Number.isNaN(parsed)) return;
        const next = splitPoints.map((sp, i) =>
            i === index ? { ...sp, startNight: parsed } : sp,
        );
        emitSplitPoints(next);
    };

    const handleDiscountPctChange = (index: number, value: string) => {
        const parsed = value === '' ? 0 : parseFloat(value);
        if (Number.isNaN(parsed)) return;
        const next = splitPoints.map((sp, i) =>
            i === index ? { ...sp, discount: parsed } : sp,
        );
        emitSplitPoints(next);
    };

    const addDiscount = () => {
        if (splitPoints.length === 0) {
            emitSplitPoints([{ startNight: 1, discount: 0 }]);
            return;
        }
        // Recovery path: if the list is in a broken state (first tier not
        // starting at night 1), prepend a tier at night 1 so the invariant
        // is restored. Normally removeDiscount protects the invariant, but
        // legacy data or save errors could leave the list in this state.
        if (splitPoints[0].startNight !== 1) {
            emitSplitPoints([{ startNight: 1, discount: 0 }, ...splitPoints]);
            return;
        }
        const last = splitPoints[splitPoints.length - 1];
        emitSplitPoints([...splitPoints, { startNight: last.startNight + 1, discount: 0 }]);
    };

    const removeDiscount = (index: number) => {
        // Drop the split point. If the removed tier was the first one
        // (covering night 1), promote the new first tier to start at
        // night 1 so the "first tier must start at 1" invariant is
        // preserved automatically — users can't accidentally break the
        // list by deleting the initial tier.
        const next = splitPoints.filter((_, i) => i !== index);
        if (index === 0 && next.length > 0 && next[0].startNight !== 1) {
            next[0] = { ...next[0], startNight: 1 };
        }
        emitSplitPoints(next);
    };

    const rangeLabel = (i: number): string => {
        const sp = splitPoints[i];
        const isLast = i === splitPoints.length - 1;
        if (isLast) {
            return t('asset_detail.fields.nights_and_above', { n: sp.startNight });
        }
        const next = splitPoints[i + 1];
        const to = next.startNight - 1;
        if (to < sp.startNight) {
            // Invalid split — the row error will explain; show a neutral hint.
            return t('asset_detail.fields.nights_from', { n: sp.startNight });
        }
        return t('asset_detail.fields.nights_range', { from: sp.startNight, to });
    };

    const showWarning = isEditing && groupAssetCount !== undefined && groupAssetCount > 1;

    // ─── Class-specific rates (#1808) ──────────────────────────────────────
    //
    // The base rows above are the GENERAL rate — `customerClassId` null, which
    // means "applies to every class". A class rate is an ADDITIONAL row of the
    // same shape carrying a class id, not a property of the general row, so
    // this block adds and removes rows rather than editing the ones above.
    //
    // Only non-default classes are offered. The system default IS the general
    // rate — a booking with no class of its own resolves to it — so giving it
    // a separate row would create two spellings of one answer, the thing the
    // organization select already avoids.
    //
    // A blank field means "no class rate", and the row is removed rather than
    // stored as 0 or NaN: an absent class row falls back to the general rate,
    // whereas a 0 row would make the segment free.
    // The array surgery lives in `classRateRows` so it can be tested — the
    // failure modes (a silently dropped rate, a duplicate scope the backend
    // rejects) are invisible by reading this component.
    const generalScales = generalRows(scales);
    // The venue sub-day unit rows (#1713) are OPTIONAL and edited in their own
    // block below — kept out of the required base map, whose fields carry a
    // star and whose labels name day types.
    const dayScales = generalScales.filter((s) => !isSubDayRow(s));
    const hasRateUnits = spaceOf(useCase)?.rateUnits === true;
    const offerableClasses = customerClasses.filter(
        (c) => c.archivedAt === null && !c.isSystemDefault,
    );

    const handleClassPriceChange = (
        classId: string,
        base: EditablePriceScale,
        raw: string,
    ) => onPriceScalesChange?.(applyClassPrice(scales, classId, base, raw));

    return (
        <AssetDetailSection title={t('asset_detail.sections.pricing')}>

            {pricesReadOnly && (
                <PriceScaleTable priceScales={priceScales} useCase={useCase} priceCategoryId={priceCategoryId} />
            )}

            {/* Base price editors */}
            {!pricesReadOnly && scales.length > 0 && (
                <div className={'mt-4 flex flex-col gap-3'}>
                    {/* Prices are stored GROSS (the customer's final price);
                        the VAT split is derived from them (#1353). Said at
                        the entry point, or operators type the wrong figure;
                        with net entry on, the fields convert (#2967). */}
                    <PriceEntryNote />
                    {dayScales.map((scale) => (
                        <NetGrossField
                            key={scale.id ?? `new-${scale.days ?? ''}-${scale.durationInMinutes}`}
                            gross={Number.isNaN(scale.price) ? '' : scale.price.toString()}
                            onGrossChange={(val) => handleScalePriceChange(scales.indexOf(scale), val)}
                            rate={rate}
                        >
                            {(input) => (
                                <InputField
                                    // A price is required on both create and edit
                                    // (explicit 0 = free), so the star shows in both.
                                    label={`${input.label(scaleLabel(scale))} *`}
                                    type={'number'}
                                    value={input.value}
                                    onChange={input.onChange}
                                    suffix={'EUR'}
                                    alignRight={true}
                                    className={'max-w-[220px]'}
                                />
                            )}
                        </NetGrossField>
                    ))}
                    {hasRateUnits && (
                        <div className={'mt-2 flex flex-col gap-2'}>
                            <div className={'flex flex-col gap-1'}>
                                <p className={'text-sm font-medium text-foreground'}>
                                    {t('asset_detail.fields.sub_day_units')}
                                </p>
                                <p className={'text-xs text-foreground/60'}>
                                    {t('asset_detail.edit.sub_day_hint')}
                                </p>
                            </div>
                            {SUB_DAY_UNITS.map((unit) => {
                                const row = subDayRowFor(scales, unit);
                                const value = row && !Number.isNaN(row.price)
                                    ? row.price.toString()
                                    : '';
                                return (
                                    <NetGrossField
                                        key={unit.key}
                                        gross={value}
                                        onGrossChange={(val) => onPriceScalesChange?.(
                                            applySubDayPrice(scales, unit, val),
                                        )}
                                        rate={rate}
                                    >
                                        {(input) => (
                                            <InputField
                                                label={input.label(t(`asset_detail.fields.price_per_${unit.key}`))}
                                                type={'number'}
                                                value={input.value}
                                                onChange={input.onChange}
                                                suffix={'EUR'}
                                                alignRight={true}
                                                className={'max-w-[220px]'}
                                            />
                                        )}
                                    </NetGrossField>
                                );
                            })}
                        </div>
                    )}
                    {offerableClasses.length > 0 && dayScales.length > 0 && (
                        <div className={'mt-2 flex flex-col gap-4'}>
                            <div className={'flex flex-col gap-1'}>
                                <p className={'text-sm font-medium text-foreground'}>
                                    {t('asset_detail.fields.class_rates')}
                                </p>
                                <p className={'text-xs text-foreground/60'}>
                                    {t('asset_detail.fields.class_rates_hint')}
                                </p>
                            </div>
                            {offerableClasses.map((cls) => (
                                <div key={cls.id} className={'flex flex-col gap-2'}>
                                    <p className={'text-sm text-foreground/80'}>{cls.name}</p>
                                    {dayScales.map((base) => {
                                        const row = classRowFor(scales, cls.id, base);
                                        const value = row && !Number.isNaN(row.price)
                                            ? row.price.toString()
                                            : '';
                                        return (
                                            <NetGrossField
                                                key={`${cls.id}-${base.days ?? ''}-${base.durationInMinutes}`}
                                                gross={value}
                                                onGrossChange={(val) => handleClassPriceChange(cls.id, base, val)}
                                                rate={rate}
                                            >
                                                {(input) => (
                                                    <InputField
                                                        label={input.label(scaleLabel(base))}
                                                        type={'number'}
                                                        value={input.value}
                                                        onChange={input.onChange}
                                                        suffix={'EUR'}
                                                        alignRight={true}
                                                        className={'max-w-[220px]'}
                                                    />
                                                )}
                                            </NetGrossField>
                                        );
                                    })}
                                </div>
                            ))}
                        </div>
                    )}
                    {showWarning && (
                        <p className={'text-xs text-foreground bg-foreground/5 border border-foreground/20 rounded-[5px] px-3 py-2'}>
                            {t('asset_detail.edit.price_warning', { count: groupAssetCount })}
                        </p>
                    )}
                    {!isStayPricing && (
                        <p className={'text-xs text-foreground bg-foreground/5 border border-foreground/20 rounded-[5px] px-3 py-2'}>
                            {t('asset_detail.edit.venue_price_floor_note')}
                        </p>
                    )}
                </div>
            )}

            {/* Discount tiers, read-only — the product's, maintained in the
                Services price dialog (option b). */}
            {isStayPricing && discountsReadOnly && (
                <div className={'mt-6'}>
                    <span className={'text-sm text-foreground/60 font-medium'}>
                        {t('asset_detail.fields.discounts')}
                    </span>
                    {hasDiscounts ? discountTable(pricingDiscounts) : (
                        <p className={'text-sm text-foreground/40 italic mt-2'}>
                            {t('asset_detail.edit.no_discounts')}
                        </p>
                    )}
                    <p className={'text-xs text-foreground/60 mt-2'}>
                        {t('asset_detail.edit.discounts_on_product')}
                    </p>
                </div>
            )}

            {/* Discount tiers — apartments only */}
            {isStayPricing && !discountsReadOnly && showDiscounts && (
                <div className={'mt-6'}>
                    <div className={'flex items-center justify-between mb-2'}>
                        <span className={'text-sm text-foreground/60 font-medium'}>
                            {t('asset_detail.fields.discounts')}{' '}
                            <span className={'text-xs font-normal text-foreground/60'}>
                                {t('asset_detail.fields.discounts_section_optional')}
                            </span>
                        </span>
                    </div>
                    {splitPoints.length > 0 ? (
                        <div className={'flex flex-col gap-3'}>
                            {splitPoints.map((sp, i) => {
                                const errKey = rowErrorKey(i);
                                const rowHasError = errKey !== null;
                                return (
                                    <div
                                        key={i}
                                        className={'flex flex-col gap-1 rounded-xl border p-3 '
                                            + (rowHasError ? 'border-red-300 bg-red-50/40' : 'border-foreground/10')}
                                    >
                                        <div className={'flex items-end gap-2'}>
                                            <div className={'flex-1'}>
                                                <InputField
                                                    label={t('asset_detail.edit.starts_at_night') + ' *'}
                                                    type={'number'}
                                                    value={sp.startNight.toString()}
                                                    onChange={(val) => handleStartChange(i, val)}
                                                />
                                            </div>
                                            <div className={'flex-1'}>
                                                <InputField
                                                    label={t('asset_detail.edit.discount_percent') + ' *'}
                                                    type={'number'}
                                                    value={sp.discount.toString()}
                                                    onChange={(val) => handleDiscountPctChange(i, val)}
                                                />
                                            </div>
                                            <button
                                                type={'button'}
                                                onClick={() => removeDiscount(i)}
                                                className={'text-red-400 hover:text-red-600 text-xl px-2 pb-2'}
                                                title={t('asset_detail.actions.remove')}
                                            >
                                                &times;
                                            </button>
                                        </div>
                                        <p className={'text-xs text-foreground/50 pl-1'}>
                                            {rangeLabel(i)}
                                        </p>
                                        {rowHasError && (
                                            <p className={'text-xs text-red-700 pl-1'}>
                                                {t(errKey as string)}
                                            </p>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        <p className={'text-sm text-foreground/40 italic'}>
                            {t('asset_detail.edit.no_discounts')}
                        </p>
                    )}
                    <button
                        type={'button'}
                        onClick={addDiscount}
                        className={'mt-3 text-sm text-primary hover:text-primary/80 font-medium'}
                    >
                        {t('asset_detail.edit.add_discount_tier')}
                    </button>
                </div>
            )}
        </AssetDetailSection>
    );
};

export default PricingSection;
